import type { ChangeEvent, ReactElement } from 'react';
import { DomainElement } from '../../../library/Core/SmartTypes';
import {
    DirectoryFilterConfigItem,
    TOP_COLLEGE_GROUP_LABEL,
    getShowProfileFilters,
    isShowProfileFilterActive,
    isCityCodeDomain,
    isCountryCodeDomain,
    partitionTopDomainOptions,
    sortDirectoryDomainItems,
    usesTopPartitionDomainGroup,
} from '../../../library/Core/SmartFunctions';

export const TOP_COUNTRY_GROUP_LABEL = 'Top Countries';
export const TOP_CITY_GROUP_LABEL = 'Top Cities';
/** Matches SelectControl second optgroup label for remaining options. */
export const DIRECTORY_COUNTRY_DIVIDER_LABEL = '----------------------';

type DirectoryFilterDomainCheckboxesProps = {
    controlId: string;
    domainCategory?: string | null;
    controlConfig?: Pick<DirectoryFilterConfigItem, 'domain' | 'domainSort' | 'domainGroup' | 'topPartitionGroupLabel'>;
    items?: DomainElement[];
    searchTerm?: string;
    isChecked: (code: string) => boolean;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    dedupeCodes?: Set<string>;
};

type DirectoryShowProfileSectionProps = {
    title: string;
    filterConfig: Record<string, DirectoryFilterConfigItem>;
    filterValues?: Record<string, unknown>;
    onBooleanChange: (filterKey: string) => (event: ChangeEvent<HTMLInputElement>) => void;
    tooltip?: string;
    listId?: string;
    mobileCollapseId?: string;
    part?: 'desktop' | 'mobile' | 'both';
};

const renderShowProfileCheckboxes = (
    filterConfig: Record<string, DirectoryFilterConfigItem>,
    filterValues: Record<string, unknown> | undefined,
    onBooleanChange: (filterKey: string) => (event: ChangeEvent<HTMLInputElement>) => void,
    layout: 'inline' | 'stack',
    idPrefix: string,
    inputClassName: string,
    labelClassName: string
) =>
    getShowProfileFilters(filterConfig).map(({ id, label }) => {
        const isInline = layout === 'inline';
        return (
            <div key={id} className={isInline ? 'form-check form-check-inline' : 'form-check'}>
                <input
                    className={isInline ? inputClassName : `${inputClassName} me-2`}
                    name="Profile_Name"
                    type="checkbox"
                    id={`${idPrefix}-${id}`}
                    checked={isShowProfileFilterActive(filterValues?.[id])}
                    onChange={onBooleanChange(id)}
                />
                <label
                    className={
                        isInline
                            ? labelClassName
                            : `${labelClassName} font-16 font-400 flex-1 ps-1 pt-1`
                    }
                    htmlFor={`${idPrefix}-${id}`}>
                    {label}
                </label>
            </div>
        );
    });

export const DirectoryShowProfileSection = ({
    title,
    filterConfig,
    filterValues,
    onBooleanChange,
    tooltip,
    listId = 'ProfileNames',
    mobileCollapseId = 'collapseshowProfile',
    part = 'both',
}: DirectoryShowProfileSectionProps): ReactElement => {
    const desktopBlock = (
        <div className="showProfiles_wrap d-none d-md-flex">
            <div className="showProfiles_left">
                <h3>{title}</h3>
                <div className="check_list check_list-single-row" id={listId}>
                    {renderShowProfileCheckboxes(
                        filterConfig,
                        filterValues,
                        onBooleanChange,
                        'inline',
                        'showProfile',
                        'form-check-input Profile_Class',
                        'form-check-label'
                    )}
                </div>
            </div>
            {tooltip && (
                <i
                    className="bi bi-info-circle-fill defaultTooltip font-16 right-10"
                    data-bs-toggle="tooltip"
                    title={tooltip}
                />
            )}
        </div>
    );

    const mobileBlock = (
        <div className="fiterItem w-40 mb-3 d-flex d-md-none">
            <div className="filterHeader">
                <div className="d-flex justify-content-between align-items-center position-relative">
                    <button
                        className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#${mobileCollapseId}`}
                        aria-expanded="false"
                        aria-controls={mobileCollapseId}>
                        <span className="font-18 font-600">{title}</span>
                    </button>
                </div>
            </div>
            <div
                id={mobileCollapseId}
                className="accordion-collapse collapse"
                data-bs-parent="#accordionFilters">
                <div className="card card-body">
                    <div className="overflow-hidden">
                        <div className="scrollMe">
                            {renderShowProfileCheckboxes(
                                filterConfig,
                                filterValues,
                                onBooleanChange,
                                'stack',
                                `${mobileCollapseId}-mobile`,
                                'form-check-input',
                                'form-check-label'
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (part === 'desktop') return desktopBlock;
    if (part === 'mobile') return mobileBlock;
    return (
        <>
            {desktopBlock}
            {mobileBlock}
        </>
    );
};

const DirectoryFilterDomainCheckboxes = ({
    controlId,
    domainCategory,
    controlConfig,
    items = [],
    searchTerm = '',
    isChecked,
    onChange,
    dedupeCodes,
}: DirectoryFilterDomainCheckboxesProps): ReactElement => {
    const term = searchTerm.toLowerCase();
    const filtered = items.filter((domain) =>
        domain.value.toLowerCase().includes(term)
    );

    const domainConfig = controlConfig ?? { domain: domainCategory ?? undefined };

    const visibleDomains =
        controlId === 'programId' && dedupeCodes
            ? filtered.filter((domain) => {
                  const code = String(domain.code);
                  if (dedupeCodes.has(code)) return false;
                  dedupeCodes.add(code);
                  return true;
              })
            : filtered;

    const renderCheckbox = (domain: DomainElement): ReactElement => (
        <div key={domain.code} className="form-check">
            <input
                id={controlId}
                className="form-check-input me-2 Passout_Class"
                type="checkbox"
                value={domain.code}
                name="Passout_Name"
                checked={isChecked(domain.code)}
                onChange={onChange}
            />
            <label
                className="form-check-label font-16 font-400 flex-1 ps-1 pt-1 defaultTooltip"
                htmlFor={`${controlId}_${domain.code}`}
                data-bs-toggle="tooltip"
                title={domain.value}>
                {domain.value}
            </label>
        </div>
    );

    if (usesTopPartitionDomainGroup(domainConfig)) {
        const { topItems, otherItems } = partitionTopDomainOptions(visibleDomains);
        const topLabel =
            controlConfig?.topPartitionGroupLabel ??
            (isCityCodeDomain(domainConfig.domain)
                ? TOP_CITY_GROUP_LABEL
                : isCountryCodeDomain(domainConfig.domain)
                  ? TOP_COUNTRY_GROUP_LABEL
                  : TOP_COLLEGE_GROUP_LABEL);
        return (
            <div className="directory-filter-country-groups">
                {topItems.length > 0 && (
                    <div className="directory-filter-optgroup">
                        <div className="directory-filter-optgroup-label">{topLabel}</div>
                        {topItems.map(renderCheckbox)}
                    </div>
                )}
                {otherItems.length > 0 && (
                    <div className="directory-filter-optgroup">
                        {topItems.length > 0 && (
                            <div className="directory-filter-optgroup-label directory-filter-optgroup-divider">
                                {DIRECTORY_COUNTRY_DIVIDER_LABEL}
                            </div>
                        )}
                        {otherItems.map(renderCheckbox)}
                    </div>
                )}
            </div>
        );
    }

    const sorted = sortDirectoryDomainItems(visibleDomains, domainConfig);
    return (
        <div className="directory-filter-checkbox-list">
            {sorted.map(renderCheckbox)}
        </div>
    );
};

export default DirectoryFilterDomainCheckboxes;
